import { gql, useLazyQuery } from '@apollo/client';
import type { CurrencyCodes } from '@onevesthq/ov-enums';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_EXCHANGE_RATE,
  FEE_BASE_CURRENCY,
} from './withdrawal.constants';

export const FETCH_FOREIGN_EXCHANGE_RATE = gql`
  query FetchForeignExchangeRate($input: FetchForeignExchangeRateInput!) {
    fetchForeignExchangeRate(input: $input) {
      rate
    }
  }
`;

type UseExchangeRateOptions = {
  organizationId?: string;
  targetCurrency?: CurrencyCodes;
  baseCurrency?: CurrencyCodes;
};

type UseExchangeRateReturn = {
  /** The exchange rate from baseCurrency to targetCurrency */
  rate: number;
  /** Whether the rate attempt for the current currency pair has finished, with or without a real rate */
  isSettled: boolean;
  /** Whether rate is above zero, including a fallback or a rate from the previous currency */
  isReady: boolean;
  /** Whether we have a real fetched rate (not a fallback default) */
  hasRealRate: boolean;
  /** Convert an amount FROM target currency TO base currency */
  convertToBaseCurrency: (amountInTargetCurrency: number) => number;
  /** Convert an amount FROM base currency TO target currency */
  convertFromBaseCurrency: (amountInBaseCurrency: number) => number;
};

const toPairKey = (from: string | undefined, to: string | undefined) =>
  `${from}-${to}`;

/**
 * Custom hook for fetching and managing exchange rates.
 *
 * Handles:
 * - Fetching rates from the API
 * - Caching rates and their inverses
 * - Graceful fallback to rate=1 on error
 * - Converting amounts between currencies
 */
export const useExchangeRate = ({
  organizationId,
  targetCurrency,
  baseCurrency = FEE_BASE_CURRENCY,
}: UseExchangeRateOptions): UseExchangeRateReturn => {
  const [rate, setRate] = useState(0);
  const [settledPair, setSettledPair] = useState<string | undefined>();
  // Keyed by currency so a rate fetched for one currency never counts as real for the next.
  const [realRateCurrency, setRealRateCurrency] = useState<
    CurrencyCodes | undefined
  >();
  const [cachedRates, setCachedRates] = useState<Map<string, number>>(
    () => new Map(),
  );
  const currentPair = toPairKey(baseCurrency, targetCurrency);
  // Responses are matched against the pair the adviser is on now, not the one they were requested for.
  const currentPairRef = useRef(currentPair);
  currentPairRef.current = currentPair;

  const setDefaultRateIfNeeded = useCallback(() => {
    setRate((currentRate) =>
      currentRate === 0 ? DEFAULT_EXCHANGE_RATE : currentRate,
    );
  }, []);

  const [fetchExchangeRate] = useLazyQuery(FETCH_FOREIGN_EXCHANGE_RATE, {
    fetchPolicy: 'network-only',
  });

  const applyFetchedRate = useCallback(
    (
      from: CurrencyCodes,
      to: CurrencyCodes,
      fetchedRate: number | undefined,
    ) => {
      const pair = toPairKey(from, to);
      if (currentPairRef.current !== pair) return;

      setSettledPair(pair);

      if (!fetchedRate) {
        setDefaultRateIfNeeded();
        return;
      }

      setCachedRates((prev) => {
        const next = new Map(prev);
        next.set(pair, fetchedRate);
        next.set(toPairKey(to, from), 1 / fetchedRate);
        return next;
      });
      setRate(fetchedRate);
      setRealRateCurrency(to);
    },
    [setDefaultRateIfNeeded],
  );

  const findCachedRate = useCallback(
    (from: CurrencyCodes, to: CurrencyCodes): number | undefined => {
      if (from === to) return DEFAULT_EXCHANGE_RATE;

      return cachedRates.get(toPairKey(from, to));
    },
    [cachedRates],
  );

  const fetchRateIfNeeded = useCallback(
    (from: CurrencyCodes, to: CurrencyCodes) => {
      const pair = toPairKey(from, to);

      // Same currency - rate is 1 and this IS a real/correct rate
      if (from === to) {
        setRate(DEFAULT_EXCHANGE_RATE);
        setRealRateCurrency(to);
        setSettledPair(pair);
        return;
      }

      // Different currencies but no organizationId - can't fetch, use default
      if (!organizationId) {
        setDefaultRateIfNeeded();
        setSettledPair(pair);
        return;
      }

      const existingRate = findCachedRate(from, to);

      if (existingRate !== undefined) {
        // Always update rate when we have a cached rate
        setRate(existingRate);
        setRealRateCurrency(to);
        setSettledPair(pair);
        return;
      }

      void fetchExchangeRate({
        variables: {
          input: {
            from,
            to,
            organizationId,
            inheritFromParents: true,
          },
        },
      })
        .then(({ data, error }) =>
          applyFetchedRate(
            from,
            to,
            error ? undefined : data?.fetchForeignExchangeRate?.rate,
          ),
        )
        .catch(() => applyFetchedRate(from, to, undefined));
    },
    [
      organizationId,
      findCachedRate,
      fetchExchangeRate,
      applyFetchedRate,
      setDefaultRateIfNeeded,
    ],
  );

  // Fetch exchange rate when target currency changes
  useEffect(() => {
    const pair = toPairKey(baseCurrency, targetCurrency);

    if (!targetCurrency) {
      // No target currency specified
      setDefaultRateIfNeeded();
      setSettledPair(pair);
      return;
    }

    if (targetCurrency === baseCurrency) {
      // Same currency - rate is 1 and this IS the correct rate
      setRate(DEFAULT_EXCHANGE_RATE);
      setRealRateCurrency(targetCurrency);
      setSettledPair(pair);
      return;
    }

    fetchRateIfNeeded(baseCurrency, targetCurrency);
  }, [targetCurrency, baseCurrency, fetchRateIfNeeded, setDefaultRateIfNeeded]);

  const convertToBaseCurrency = useCallback(
    (amountInTargetCurrency: number): number => {
      if (rate <= 0) return amountInTargetCurrency;

      return Math.round(amountInTargetCurrency / rate);
    },
    [rate],
  );

  const convertFromBaseCurrency = useCallback(
    (amountInBaseCurrency: number): number => {
      if (rate <= 0) return amountInBaseCurrency;

      return Math.round(amountInBaseCurrency * rate);
    },
    [rate],
  );

  const isReady = rate > 0;
  const hasRealRate =
    targetCurrency !== undefined && realRateCurrency === targetCurrency;

  return {
    rate,
    isSettled: settledPair === currentPair,
    isReady,
    hasRealRate,
    convertToBaseCurrency,
    convertFromBaseCurrency,
  };
};
